import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { toast } from "sonner";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { api, formatApiError } from "@/lib/api";
import { FolderIcon, folderColorClasses } from "@/components/folderTheme";
import AddToFolderDropdown from "@/components/AddToFolderDropdown";

/**
 * Phase FOLDER-UX (FU.1) — Inline folder-name chips for the Folders
 * column on the workflow library table.
 *
 * Props:
 *   workflowId         the row this is rendering for
 *   workflowSlug       used in data-testids for stable selectors
 *   workflowName       used in the AddToFolderDropdown trigger label
 *   summary            { count, names } from useFolderSummaryByWorkflows;
 *                      `names` is the cheap label-only payload, so we
 *                      fetch the FULL folder docs (with folder_id + color +
 *                      icon) lazily only when count > 0
 *   onRemoved          optional callback after a chip is removed; the
 *                      parent should invalidate the folder-summary query
 *
 * Empty state: shows an em-dash + a small "+" affordance that opens
 *              the same AddToFolderDropdown the per-row kebab uses.
 * Non-empty:   up to 2 chips, then "+N more" overflow with tooltip
 *              listing the remaining folder names.
 */
export default function FolderChipList({
  workflowId,
  workflowSlug,
  workflowName,
  summary,
  onRemoved,
}) {
  const count = summary?.count ?? 0;

  // Only fetch the per-workflow folder list when we have at least one
  // membership — keeps the network footprint flat for empty rows.
  const membersQuery = useQuery({
    queryKey: ["workflow-folders", workflowId],
    queryFn: async () => (await api.get(`/workflows/${workflowId}/folders`)).data,
    enabled: count > 0,
    staleTime: 30_000,
  });

  const folders = useMemo(
    () => membersQuery.data?.folders || [],
    [membersQuery.data],
  );

  async function removeChip(folderId, folderName) {
    try {
      await api.delete(`/folders/${folderId}/workflow-items/${workflowId}`);
      toast.success(`Removed from "${folderName}"`);
      if (onRemoved) onRemoved();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  if (count === 0) {
    return (
      <AddToFolderDropdown workflowId={workflowId}>
        <button
          type="button"
          className="group inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-[11px] text-slate-300 transition-colors hover:bg-indigo-50 hover:text-indigo-600"
          aria-label={`Add ${workflowName} to a folder`}
          data-testid={`folder-chip-empty-${workflowSlug}`}
        >
          <span>—</span>
          <span className="grid h-4 w-4 place-items-center rounded border border-dashed border-slate-300 text-[10px] font-semibold group-hover:border-indigo-300 group-hover:text-indigo-500">
            +
          </span>
        </button>
      </AddToFolderDropdown>
    );
  }

  // Defer rendering of chips until the folder list lands; show a tiny
  // placeholder pill in the meantime so the cell doesn't reflow.
  if (membersQuery.isLoading || folders.length === 0) {
    return (
      <span
        className="inline-flex h-5 w-12 animate-pulse rounded-full bg-slate-100"
        data-testid={`folder-chip-loading-${workflowSlug}`}
      />
    );
  }

  const visible = folders.slice(0, 2);
  const overflow = folders.length > 2 ? folders.slice(2) : [];

  return (
    <TooltipProvider delayDuration={200}>
      <div
        className="flex flex-wrap items-center gap-1"
        data-testid={`folder-chips-${workflowSlug}`}
      >
        {visible.map((f) => {
          const palette = folderColorClasses(f.color);
          return (
            <span
              key={f.folder_id}
              className={[
                "group inline-flex max-w-[140px] items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-medium",
                palette.bg,
                palette.text,
                palette.border,
              ].join(" ")}
              data-testid={`folder-chip-${workflowSlug}-${f.folder_id}`}
              title={f.name}
            >
              <FolderIcon name={f.icon} size={9} />
              <span className="truncate">{f.name}</span>
              {f.can_modify_items && (
                <button
                  type="button"
                  onClick={() => removeChip(f.folder_id, f.name)}
                  aria-label={`Remove ${workflowName} from ${f.name}`}
                  className="-mr-0.5 ml-0.5 grid h-3 w-3 place-items-center rounded-full opacity-0 transition-opacity hover:bg-white/70 group-hover:opacity-100"
                  data-testid={`folder-chip-remove-${workflowSlug}-${f.folder_id}`}
                >
                  <X size={8} strokeWidth={3} />
                </button>
              )}
            </span>
          );
        })}
        {overflow.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <AddToFolderDropdown
                workflowId={workflowId}
              >
                <button
                  type="button"
                  className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-200"
                  data-testid={`folder-chip-overflow-${workflowSlug}`}
                >
                  +{overflow.length} more
                </button>
              </AddToFolderDropdown>
            </TooltipTrigger>
            <TooltipContent side="top" align="start" className="max-w-[240px] text-[11px]">
              <ul className="space-y-0.5">
                {overflow.map((f) => (
                  <li key={f.folder_id} className="truncate">
                    • {f.name}
                  </li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
